<template>
  <div class="presave-campaign-detail">
    <!-- Loading State -->
    <div
      v-if="loading"
      class="loading-container"
    >
      <IndifyLoader />
    </div>

    <!-- Main Content -->
    <div
      v-else-if="release._id"
      class="campaign-container"
    >
      <!-- Enhanced Background -->
      <div class="background-layers">
        <!-- Blurred cover background -->
        <div
          class="background-image"
          :style="{ backgroundImage: `url(${release.cover_image})` }"
        />

        <!-- Dynamic gradient overlay -->
        <div class="gradient-overlay" />

        <!-- Subtle animated elements -->
        <div class="ambient-effects">
          <div class="glow-orb glow-1" />
          <div class="glow-orb glow-2" />
          <div class="glow-orb glow-3" />
        </div>
      </div>

      <!-- Content -->
      <div class="campaign-content">
        <!-- Pre-save Success State -->
        <div
          v-if="subscribed"
          class="success-state"
        >
          <div class="success-wrapper">
            <div class="success-icon">
              <i class="fas fa-check-circle" />
            </div>
            <h1 class="success-title">
              {{ $t('presave.thank-you') }}
            </h1>
            <p class="success-message">
              {{ $t('presave.notification-message') }}
            </p>
            <div class="success-actions">
              <BaseButton
                variant="primary"
                size="large"
                class="glass-button"
                @click="sharePresave"
              >
                <i class="fas fa-share-alt mr-2" />
                {{ $t('presave.share') }}
              </BaseButton>
            </div>
          </div>
        </div>

        <!-- Pre-save Form State -->
        <div
          v-else
          class="presave-state"
        >
          <!-- Hero Section -->
          <div class="hero-section">
            <div class="artist-badge">
              <i class="fas fa-music mr-2" />
              {{ release.artist?.name }}
            </div>

            <h1 class="release-title">
              {{ release.title }}
            </h1>

            <div class="release-meta">
              <div class="release-date-badge">
                <i class="fas fa-calendar-alt mr-2" />
                {{ $t('presave.available-on') }}
                <strong>{{ formatReleaseDate(release.release_date) }}</strong>
              </div>
            </div>
          </div>

          <!-- Cover Art Showcase -->
          <div class="cover-showcase">
            <div class="cover-container">
              <div class="cover-frame">
                <img
                  ref="coverImageRef"
                  :src="release.cover_image_small || release.cover_image"
                  :alt="release.title"
                  class="cover-image"
                  crossorigin="anonymous"
                  @load="onCoverLoad"
                >
                <div class="cover-reflection" />
              </div>

              <!-- Floating music notes -->
              <div class="music-notes">
                <i class="fas fa-music note-1" />
                <i class="fas fa-music note-2" />
                <i class="fas fa-music note-3" />
              </div>
            </div>
          </div>

          <!-- Action Section -->
          <div class="action-section">
            <div class="main-action">
              <BaseButton
                variant="spotify"
                size="large"
                :loading="authenticating"
                class="spotify-button-main"
                @click="loginSpotify"
              >
                <i class="fab fa-spotify mr-3" />
                {{ $t('presave.save-on-spotify') }}
              </BaseButton>
            </div>

            <p class="action-description">
              {{ $t('presave.description') }}
            </p>

            <!-- Benefits Grid -->
            <div class="benefits-grid">
              <div class="benefit-item">
                <div class="benefit-icon">
                  <i class="fas fa-bell" />
                </div>
                <span>Instant notification</span>
              </div>
              <div class="benefit-item">
                <div class="benefit-icon">
                  <i class="fas fa-heart" />
                </div>
                <span>Auto-add to library</span>
              </div>
              <div class="benefit-item">
                <div class="benefit-icon">
                  <i class="fas fa-users" />
                </div>
                <span>Join the community</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else
      class="error-container"
    >
      <div class="error-content">
        <div class="error-icon-wrapper">
          <i class="fas fa-exclamation-triangle error-icon" />
        </div>
        <h2 class="error-title">
          {{ $t('presave.not-found') }}
        </h2>
        <p class="error-message">
          {{ $t('presave.not-found-message') }}
        </p>
        <BaseButton
          variant="primary"
          size="large"
          @click="goHome"
        >
          {{ $t('general.go-home') }}
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import IndifyLoader from '@/components/ui/IndifyLoader.vue';
import BaseButton from '@/components/ui/BaseButton.vue';
import releaseService from '@/services/releaseService';

export default {
  name: 'PublicPresaveView',
  components: {
    IndifyLoader,
    BaseButton,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { t, locale } = useI18n();

    // Reactive state
    const loading = ref(true);
    const release = ref({});
    const subscribed = ref(false);
    const authenticating = ref(false);
    const coverLoaded = ref(false);
    const coverImageRef = ref(null);
    const dominantColor = ref('#3e8ed0');
    const accentColor = ref('#8b5cf6');

    // Computed
    const apiURL = computed(() => {
      return location.href.match(/localhost/)
        ? 'http://localhost:3000/'
        : 'https://indiefy.net/';
    });

    const spotifyRedirectURL = computed(() => {
      return location.href.match(/localhost/)
        ? 'http://localhost:8080/spotify-auth'
        : 'https://indiefy.net/spotify-auth';
    });

    // Helper function to set meta tags
    const setMetaTag = (name, content, property = false) => {
      let element = property
        ? document.querySelector(`meta[property="${name}"]`)
        : document.querySelector(`meta[name="${name}"]`);

      if (!element) {
        element = document.createElement('meta');
        if (property) {
          element.setAttribute('property', name);
        } else {
          element.setAttribute('name', name);
        }
        document.head.appendChild(element);
      }

      element.setAttribute('content', content);
    };

    // Color extraction with CORS handling
    const extractColorsFromImage = () => {
      if (!coverImageRef.value) return;

      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = coverImageRef.value;

        // Use smaller canvas for performance
        const size = 50;
        canvas.width = size;
        canvas.height = size;
        ctx.drawImage(img, 0, 0, size, size);

        const imageData = ctx.getImageData(0, 0, size, size);
        const data = imageData.data;
        const colorCounts = {};

        // Sample pixels efficiently
        for (let i = 0; i < data.length; i += 32) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          const alpha = data[i + 3];

          if (alpha > 128) {
            const color = `rgb(${r},${g},${b})`;
            colorCounts[color] = (colorCounts[color] || 0) + 1;
          }
        }

        const sortedColors = Object.entries(colorCounts)
          .sort(([,a], [,b]) => b - a)
          .slice(0, 5);

        if (sortedColors.length >= 2) {
          dominantColor.value = sortedColors[0][0];
          accentColor.value = sortedColors[1][0];
        }
      } catch (error) {
        console.log('Could not extract colors from image (CORS):', error);
        // Use default colors if CORS blocks extraction
      }
    };

    // Methods
    const formatReleaseDate = (dateStr) => {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      };
      return date.toLocaleDateString(locale.value, options);
    };

    const onCoverLoad = () => {
      coverLoaded.value = true;
      // Try to extract colors with delay
      setTimeout(extractColorsFromImage, 300);
    };

    const followSpotifyArtist = async (accessToken, artistId) => {
      try {
        await fetch(`https://api.spotify.com/v1/me/following?type=artist&ids=${artistId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });
      } catch (error) {
        console.error('Error following artist:', error);
      }
    };

    const getUserData = async (accessToken, refreshToken) => {
      try {
        const response = await fetch('https://api.spotify.com/v1/me', {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        });

        const userData = await response.json();

        if (localStorage.getItem('targetCampaign')) {
          subscribed.value = true;

          // Subscribe to presave campaign
          await fetch(`${apiURL.value}v1/api/presave-campaigns/${release.value._id}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              spotify_id: userData.id,
              subscriber: userData,
              spotify_token: accessToken,
              refresh_token: refreshToken,
            }),
          });

          // Follow artists
          if (release.value.artist?.spotify_id) {
            await followSpotifyArtist(accessToken, release.value.artist.spotify_id);
          }

          setTimeout(async () => {
            await followSpotifyArtist(accessToken, '2qG9ClNOYgpKl8bFL8rID6');
          }, 200);

          localStorage.removeItem('targetCampaign');
        }
      } catch (error) {
        console.error('Error getting user data:', error);
        localStorage.removeItem('targetCampaign');
      }
    };

    const loginSpotify = () => {
      const CLIENT_ID = '6e4a92b2f27b47de827b03198075ee37';
      const REDIRECT_URI = spotifyRedirectURL.value;

      authenticating.value = true;

      const scopes = [
        'user-read-email',
        'user-follow-modify',
        'user-library-modify',
        'playlist-modify-public',
      ];

      const url = 'https://accounts.spotify.com/authorize?' +
        `client_id=${CLIENT_ID}&` +
        `redirect_uri=${encodeURIComponent(REDIRECT_URI)}&` +
        `scope=${encodeURIComponent(scopes.join(' '))}&` +
        'response_type=code';

      localStorage.setItem('targetCampaign', release.value.key);
      window.location.href = url;

      setTimeout(() => {
        authenticating.value = false;
      }, 500);
    };

    const sharePresave = () => {
      if (navigator.share) {
        navigator.share({
          title: `Presave ${release.value.title}`,
          text: `Check out this upcoming release: ${release.value.title}`,
          url: window.location.href,
        });
      } else {
        // Fallback: copy to clipboard
        navigator.clipboard.writeText(window.location.href);
        // You could show a toast notification here
      }
    };

    const goHome = () => {
      router.push('/');
    };

    // Lifecycle
    onMounted(async () => {
      try {
        const response = await releaseService.getPublicRelease(route.params.key);

        if (!response) {
          loading.value = false;
          return;
        }

        release.value = response;

        // Update page title and meta tags
        const title = `Presave ${release.value.title} on Spotify`;
        document.title = title;
        setMetaTag('og:title', title, true);
        setMetaTag('description', `Presave ${release.value.title} by ${release.value.artist?.name} on Spotify`);

        // If release is already published, redirect to release page
        if (release.value.status === 3) {
          const redirectUrl = release.value.key
            ? `https://indfy.me/${release.value.key}`
            : `https://music.indiefy.net/releases/${release.value._id}`;
          window.location.href = redirectUrl;
          return;
        }

        // Check if user is returning from Spotify auth
        if (localStorage.getItem('targetCampaign')) {
          const accessToken = localStorage.getItem('access_token');
          const refreshToken = localStorage.getItem('refresh_token');

          if (accessToken) {
            await getUserData(accessToken, refreshToken);
          }
        }

        loading.value = false;
      } catch (error) {
        console.error('Error loading presave campaign:', error);
        loading.value = false;
      }
    });

    return {
      loading,
      release,
      subscribed,
      authenticating,
      coverLoaded,
      coverImageRef,
      dominantColor,
      accentColor,
      formatReleaseDate,
      onCoverLoad,
      loginSpotify,
      sharePresave,
      goHome,
      t,
    };
  },
};
</script>

<style lang="scss" scoped>
.presave-campaign-detail {
  min-height: 100vh;
  position: relative;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  color: white !important;

  * {
    color: white !important;
  }

  h1, h2, h3, h4, h5, h6, p, span, div {
    color: white !important;
  }
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.campaign-container {
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 2rem 1rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

// Enhanced Background System
.background-layers {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  overflow: hidden;
}

.background-image {
  position: absolute;
  top: -10%;
  left: -10%;
  width: 120%;
  height: 120%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  filter: blur(30px) brightness(0.3) saturate(1.2);
  transform: scale(1.1);
}

.gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(ellipse at center,
    rgba(62, 142, 208, 0.1) 0%,
    rgba(139, 92, 246, 0.2) 40%,
    rgba(0,0,0,0.85) 100%
  ), linear-gradient(135deg,
    rgba(62, 142, 208, 0.05),
    rgba(139, 92, 246, 0.15),
    rgba(0,0,0,0.9)
  );
}

// Subtle ambient effects
.ambient-effects {
  position: absolute;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(40px);
  opacity: 0.3;
  animation: float 8s ease-in-out infinite;

  &.glow-1 {
    width: 300px;
    height: 300px;
    top: 20%;
    left: -10%;
    animation-delay: 0s;
    background-color: rgba(62, 142, 208, 0.15);
  }

  &.glow-2 {
    width: 200px;
    height: 200px;
    bottom: 30%;
    right: -5%;
    animation-delay: 3s;
    background-color: rgba(139, 92, 246, 0.15);
  }

  &.glow-3 {
    width: 150px;
    height: 150px;
    top: 60%;
    left: 20%;
    animation-delay: 6s;
    background-color: rgba(62, 142, 208, 0.1);
  }
}

.campaign-content {
  max-width: 500px;
  width: 100%;
  text-align: center;
  color: white !important;
  z-index: 1;
  position: relative;

  * {
    color: white !important;
  }
}

// Success State
.success-state {
  opacity: 0;
  animation: fadeInScale 0.8s ease-out forwards;
}

.success-wrapper {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-radius: 24px;
  padding: 3rem 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.success-icon {
  font-size: 4rem;
  color: #1db954;
  margin-bottom: 1.5rem;
  filter: drop-shadow(0 4px 8px rgba(29, 185, 84, 0.3));
}

.success-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1rem;
  color: white !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.success-message {
  font-size: 1.1rem;
  color: white !important;
  line-height: 1.6;
  margin-bottom: 2rem;
}

// Main Presave State
.presave-state {
  opacity: 0;
  animation: fadeInUp 0.8s ease-out forwards;
}

// Hero Section
.hero-section {
  margin-bottom: 3rem;
}

.artist-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  color: white !important;
  margin-bottom: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.release-title {
  font-size: 2.5rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  color: white !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
}

.release-meta {
  margin-bottom: 2rem;
}

.release-date-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  padding: 0.75rem 1.5rem;
  border-radius: 25px;
  font-size: 0.95rem;
  color: white !important;
  border: 1px solid rgba(255, 255, 255, 0.3);

  strong {
    color: white !important;
    font-weight: 600;
  }
}

// Cover Showcase
.cover-showcase {
  margin: 3rem 0;
  position: relative;
}

.cover-container {
  position: relative;
  display: inline-block;
}

.cover-frame {
  position: relative;
  display: inline-block;
}

.cover-image {
  width: 280px;
  max-width: 85vw;
  height: auto;
  border-radius: 20px;
  box-shadow:
    0 25px 50px rgba(0, 0, 0, 0.5),
    0 0 0 6px rgba(255, 255, 255, 0.1),
    0 0 0 12px rgba(255, 255, 255, 0.05);
  transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);

  &:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow:
      0 35px 70px rgba(0, 0, 0, 0.6),
      0 0 0 8px rgba(255, 255, 255, 0.15),
      0 0 0 16px rgba(255, 255, 255, 0.08);
  }

  @media (max-width: 768px) {
    width: 240px;
  }
}

.cover-reflection {
  position: absolute;
  bottom: -40px;
  left: 0;
  width: 100%;
  height: 40px;
  background: linear-gradient(to bottom,
    rgba(255, 255, 255, 0.1) 0%,
    transparent 100%);
  border-radius: 0 0 20px 20px;
  transform: scaleY(-1);
  opacity: 0.3;
  filter: blur(1px);
}

// Floating music notes
.music-notes {
  position: absolute;
  width: 100%;
  height: 100%;
  pointer-events: none;

  i {
    position: absolute;
    color: rgba(255, 255, 255, 0.2);
    font-size: 1.2rem;
    animation: floatNote 6s ease-in-out infinite;
  }

  .note-1 {
    top: 20%;
    right: -40px;
    animation-delay: 0s;
  }

  .note-2 {
    bottom: 30%;
    left: -40px;
    animation-delay: 2s;
  }

  .note-3 {
    top: 60%;
    right: -30px;
    animation-delay: 4s;
  }
}

// Action Section
.action-section {
  margin-top: 3rem;
}

.main-action {
  margin-bottom: 1.5rem;
}

.spotify-button-main {
  background: linear-gradient(135deg, #1db954 0%, #1ed760 100%) !important;
  border: none !important;
  font-size: 1.1rem;
  font-weight: 700;
  padding: 1rem 2.5rem;
  border-radius: 50px;
  box-shadow:
    0 8px 25px rgba(29, 185, 84, 0.4),
    0 0 0 2px rgba(255, 255, 255, 0.1);
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
  overflow: hidden;
  color: white !important;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg,
      transparent,
      rgba(255, 255, 255, 0.2),
      transparent);
    transition: left 0.5s ease;
  }

  &:hover {
    background: linear-gradient(135deg, #1ed760 0%, #17c653 100%) !important;
    transform: translateY(-3px);
    box-shadow:
      0 12px 35px rgba(29, 185, 84, 0.6),
      0 0 0 4px rgba(255, 255, 255, 0.2);

    &::before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 0.875rem 2rem;
  }
}

.action-description {
  font-size: 1rem;
  color: white !important;
  line-height: 1.6;
  margin-bottom: 2rem;
}

// Benefits Grid
.benefits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 1rem;
  margin-top: 2rem;
}

.benefit-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  padding: 1.5rem 1rem;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.25);
    transform: translateY(-2px);
  }
}

.benefit-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
  color: #3e8ed0;
  font-size: 1.1rem;
}

.benefit-item span {
  font-size: 0.85rem;
  color: white !important;
  font-weight: 500;
}

// Glass button effect
.glass-button {
  background: rgba(255, 255, 255, 0.15) !important;
  backdrop-filter: blur(20px) !important;
  -webkit-backdrop-filter: blur(20px) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  color: white !important;

  &:hover {
    background: rgba(255, 255, 255, 0.25) !important;
    transform: translateY(-2px);
  }
}

// Error State
.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.error-content {
  text-align: center;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  padding: 3rem 2rem;
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.error-icon-wrapper {
  margin-bottom: 2rem;
}

.error-icon {
  font-size: 3.5rem;
  color: #ff6b6b;
  filter: drop-shadow(0 4px 8px rgba(255, 107, 107, 0.3));
}

.error-title {
  font-size: 1.8rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: white;
}

.error-message {
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 2rem;
  line-height: 1.6;
}

// Optimized Animations
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) translateX(0px);
  }
  33% {
    transform: translateY(-20px) translateX(10px);
  }
  66% {
    transform: translateY(-10px) translateX(-10px);
  }
}

@keyframes floatNote {
  0%, 100% {
    transform: translateY(0px);
    opacity: 0.2;
  }
  50% {
    transform: translateY(-15px);
    opacity: 0.4;
  }
}

// Mobile optimizations
@media (max-width: 768px) {
  .campaign-content {
    padding: 1rem;
  }

  .hero-section {
    margin-bottom: 2rem;
  }

  .cover-showcase {
    margin: 2rem 0;
  }

  .benefits-grid {
    grid-template-columns: 1fr;
    gap: 0.75rem;
  }

  .benefit-item {
    flex-direction: row;
    text-align: left;
    padding: 1rem;
  }

  .benefit-icon {
    margin-bottom: 0;
    margin-right: 0.75rem;
    width: 35px;
    height: 35px;
  }

  .music-notes {
    display: none;
  }
}

// Reduce motion for accessibility
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>